import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { FormatBold as Bold, Link, Code } from "@mui/icons-material";

export default function BlogTextEditor({ value, onChange, placeholder }) {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [savedRange, setSavedRange] = useState(null);
  const [isBold, setIsBold] = useState(false);
  const editableRef = useRef(null);
  const isInternalUpdate = useRef(false);

  // Only set innerHTML on initial mount or when value changes externally
  useEffect(() => {
    if (editableRef.current && !isInternalUpdate.current) {
      if (editableRef.current.innerHTML !== value) {
        editableRef.current.innerHTML = value;
      }
    }
    isInternalUpdate.current = false;
  }, [value]);

  const handleInput = () => {
    if (editableRef.current) {
      isInternalUpdate.current = true;
      onChange(editableRef.current.innerHTML);
    }
    updateToolbarState();
  };

  const updateToolbarState = () => {
    setIsBold(document.queryCommandState("bold"));
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setSavedRange(selection.getRangeAt(0).cloneRange());
    }
  };

  const restoreSelection = () => {
    if (savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange);
    }
  };

  const handleBold = () => {
    document.execCommand("bold", false, null);
    if (editableRef.current) {
      isInternalUpdate.current = true;
      onChange(editableRef.current.innerHTML);
    }
    // Update state immediately after executing command
    setIsBold(document.queryCommandState("bold"));
  };

  const handleLinkClick = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString();

    saveSelection();
    setLinkText(selectedText);
    setLinkUrl("");
    setLinkDialogOpen(true);
  };

  const handleLinkInsert = () => {
    if (!linkUrl) return;

    // Sanitize URL - basic validation
    const sanitizedUrl = linkUrl.trim();
    if (!sanitizedUrl.match(/^https?:\/\//i) && !sanitizedUrl.startsWith("/")) {
      alert("Please enter a valid URL starting with http://, https://, or /");
      return;
    }

    restoreSelection();

    // Create link element properly instead of using insertHTML with string
    const link = document.createElement("a");
    link.href = sanitizedUrl;
    link.style.color = "#1976d2";
    link.style.textDecoration = "underline";
    link.textContent = linkText || sanitizedUrl;

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      range.insertNode(link);

      // Move cursor after the link
      range.setStartAfter(link);
      range.setEndAfter(link);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    if (editableRef.current) {
      isInternalUpdate.current = true;
      onChange(editableRef.current.innerHTML);
    }

    setLinkDialogOpen(false);
    setLinkText("");
    setLinkUrl("");
    setSavedRange(null);
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.23)",
        borderRadius: 1,
        padding: 2,
        transition: "border-color 200ms",
        "&:hover": {
          borderColor: "rgba(0, 0, 0, 0.87)",
        },
        "&:focus-within": {
          borderColor: "primary.main",
          borderWidth: 2,
        },
      }}
    >
      <Toolbar
        sx={{ gap: 1, borderBottom: "1px solid #e0e0e0", flexWrap: "wrap" }}
      >
        <IconButton
          onClick={handleBold}
          color="primary"
          title="Bold (Ctrl+B)"
          size="small"
          sx={{
            backgroundColor: isBold ? "primary.main" : "transparent",
            color: isBold ? "white" : "primary.main",
            "&:hover": {
              backgroundColor: isBold
                ? "primary.dark"
                : "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          <Bold />
        </IconButton>

        <IconButton
          onClick={handleLinkClick}
          color="primary"
          title="Insert Link"
          size="small"
        >
          <Link />
        </IconButton>

        <Box sx={{ flexGrow: 1 }} />
      </Toolbar>

      <Box
        ref={editableRef}
        contentEditable
        onInput={handleInput}
        onMouseUp={updateToolbarState}
        onKeyUp={updateToolbarState}
        suppressContentEditableWarning
        sx={{
          minHeight: 200,
          p: 2,
          outline: "none",
          fontFamily: "Roboto, sans-serif",
          fontSize: "16px",
          lineHeight: 1.6,
          cursor: "text",
          overflowWrap: "break-word",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          maxWidth: "100%",
          "&:empty:before": {
            content: `"${placeholder}"`,
            color: "#999",
          },
          "& a": {
            color: "#1976d2",
            textDecoration: "underline",
            cursor: "pointer",
          },
          "& strong, & b": {
            fontWeight: "bold",
          },
        }}
      />

      <Dialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Link Text
              </Typography>
              <input
                autoFocus
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Enter link text"
                style={{
                  width: "100%",
                  padding: "12px",
                  fontSize: "16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontFamily: "Roboto, sans-serif",
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                URL
              </Typography>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                style={{
                  width: "100%",
                  padding: "12px",
                  fontSize: "16px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontFamily: "Roboto, sans-serif",
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleLinkInsert();
                  }
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleLinkInsert}
            variant="contained"
            disabled={!linkUrl}
          >
            Insert
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
